import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DepotSummary, TenantProfile, UserAccount } from '../../../models/userManagement';
import { userManagementApi } from '../../../api/userManagementApi';

interface DepotUsersDrawerProps {
  open: boolean;
  depot?: DepotSummary;
  tenants: TenantProfile[];
  users: UserAccount[];
  onClose: () => void;
}

const DepotUsersDrawer: React.FC<DepotUsersDrawerProps> = ({ open, depot, tenants, users, onClose }) => {
  const [assignedUsers, setAssignedUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');

  const availableUsers = useMemo(() => {
    if (!depot) {
      return [];
    }
    return users.filter((user) => user.tenantId === depot.tenantId && user.active);
  }, [users, depot]);

  const loadAssignments = useCallback(async () => {
    if (!depot) {
      setAssignedUsers([]);
      return;
    }

    setLoading(true);
    try {
      const assignments = await userManagementApi.listDepotUsers(depot.depotId);
      setAssignedUsers(assignments.map((assignment) => assignment.email));
    } catch (error) {
      console.error('[DepotUsersDrawer] Failed to load assignments', error);
      setAssignedUsers([]);
    } finally {
      setLoading(false);
    }
  }, [depot]);

  useEffect(() => {
    if (open) {
      loadAssignments();
    }
  }, [loadAssignments, open]);

  const handleAssign = async () => {
    if (!depot || !selectedEmail) {
      return;
    }
    await userManagementApi.assignDepotUser(depot.depotId, selectedEmail);
    setSelectedEmail('');
    loadAssignments();
  };

  const handleRemove = async (email: string) => {
    if (!depot) {
      return;
    }
    await userManagementApi.removeDepotUser(depot.depotId, email);
    loadAssignments();
  };

  if (!depot) {
    return null;
  }

  const tenantName = tenants.find((tenant) => tenant.id === depot.tenantId)?.name || depot.tenantId;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 360 } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
        <Typography variant="h6">Manage Depot Access</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box px={2} py={2}>
        <Typography variant="subtitle2" color="text.secondary">
          {tenantName}
        </Typography>
        <Typography variant="h6" gutterBottom>
          {depot.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {depot.depotId} • {depot.modelType}
        </Typography>
      </Box>
      <Divider />
      <Box px={2} py={2}>
        <Stack direction="row" spacing={1}>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={selectedEmail}
            onChange={(event) => setSelectedEmail(event.target.value)}
          >
            <MenuItem value="" disabled>
              Select user
            </MenuItem>
            {availableUsers.map((user) => (
              <MenuItem key={user.email} value={user.email}>
                {user.name || user.email}
              </MenuItem>
            ))}
          </Select>
          <Button variant="contained" disabled={!selectedEmail} onClick={handleAssign}>
            Add
          </Button>
        </Stack>
      </Box>
      <Divider />
      <Box px={2} py={2} flex={1} display="flex" flexDirection="column">
        <Typography variant="subtitle2" gutterBottom>
          Assigned Users
        </Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        ) : (
          <List dense disablePadding>
            {assignedUsers.map((email) => (
              <ListItem key={email} disableGutters>
                <ListItemText primary={users.find((user) => user.email === email)?.name || email} secondary={email} />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => handleRemove(email)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {assignedUsers.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No users assigned yet.
              </Typography>
            )}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

export default DepotUsersDrawer;
